import React, { useState } from 'react';
import PropTypes from 'prop-types';
// Material-UI
import Modal from '@material-ui/core/Modal';
import IconButton from '@material-ui/core/IconButton';
import Button from '@material-ui/core/Button';
import TextField from '@material-ui/core/TextField';
import InputAdornment from '@material-ui/core/InputAdornment';
import FormControl from '@material-ui/core/FormControl';
import FormLabel from '@material-ui/core/FormLabel';
import FormGroup from '@material-ui/core/FormGroup';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import FormHelperText from '@material-ui/core/FormHelperText';
import Checkbox from '@material-ui/core/Checkbox';
import Select from '@material-ui/core/Select';
import MenuItem from '@material-ui/core/MenuItem';
import InputLabel from '@material-ui/core/InputLabel';
import Chip from '@material-ui/core/Chip';
// Icons
import { X, User } from 'tabler-icons-react';
import Visibility from '@material-ui/icons/Visibility';
import VisibilityOff from '@material-ui/icons/VisibilityOff';
// Context
import { useUsuarios } from './UsuariosContext';

export default function ModalNuevoUsuario({ open, onClose }) {
    const {
        roles,
        setRoles,
        ordenaArrObj,
        gpos,
        deptos,
        languages,
        registeruser,
        passError,
        validaPass,
    } = useUsuarios();

    const [form, setForm] = useState({
        usuario: '',
        password: '',
        nombre: '',
        apellido: '',
        email: '',
        phoneNumber: '',
        notas: '',
    });
    const [showPassword, setShowPassword] = useState(false);
    const [gpo, setGpo] = useState('');
    const [depto, setDepto] = useState('');
    const [language, setLanguage] = useState('');
    const [levels, setLevels] = useState({ one: true, two: false, three: false });

    const set = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));

    const handlePassChange = (e) => {
        set('password')(e);
        validaPass(e.target.value);
    };

    const toggleRole = (name) => {
        const newRoles = roles.map((rol) =>
            rol.nombre === name
                ? { ...rol, color: rol.color === 'primary' ? 'default' : 'primary' }
                : rol
        );
        newRoles.sort(ordenaArrObj);
        setRoles(newRoles);
    };

    const handleClose = () => {
        setForm({
            usuario: '',
            password: '',
            nombre: '',
            apellido: '',
            email: '',
            phoneNumber: '',
            notas: '',
        });
        setGpo('');
        setDepto('');
        setLanguage('');
        setLevels({ one: true, two: false, three: false });
        onClose();
    };

    const createUser = () => {
        registeruser({
            usuario: form.usuario,
            password: form.password,
            email: form.email,
            phoneNumber: form.phoneNumber,
            id: null,
            nombre: form.nombre,
            apellido: form.apellido,
            cardId: null,
            notas: form.notas,
            departamento: depto,
            grupo: gpo,
            esNivel1: levels.one,
            esNivel2: levels.two,
            esNivel3: levels.three,
            idioma: language,
            horario: null,
            roles: roles.filter((r) => r.color === 'primary').map((r) => r.nombre),
        });
        handleClose();
    };

    const showPassHint = form.password.length > 0;

    return (
        <Modal open={open} onClose={handleClose}>
            <div className="usuarios-modal usuarios-modal--wide">
                <div className="usuarios-modal-header">
                    <h2 className="usuarios-modal-title">Nuevo usuario</h2>
                    <IconButton size="small" onClick={handleClose}>
                        <X size={18} strokeWidth={2} />
                    </IconButton>
                </div>

                <div className="usuarios-modal-body">
                    {/* ── Acceso ── */}
                    <div className="usuarios-form-section">
                        <p className="usuarios-form-section-title">Acceso</p>
                        <div className="usuarios-form-grid">
                            <TextField
                                label="Nombre de usuario"
                                value={form.usuario}
                                onChange={set('usuario')}
                                fullWidth
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <User size={18} />
                                        </InputAdornment>
                                    ),
                                }}
                            />
                            <TextField
                                label="Contraseña"
                                type={showPassword ? 'text' : 'password'}
                                value={form.password}
                                onChange={handlePassChange}
                                error={showPassHint && passError}
                                fullWidth
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton
                                                size="small"
                                                onClick={() => setShowPassword((p) => !p)}
                                            >
                                                {showPassword ? <VisibilityOff /> : <Visibility />}
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                }}
                            />
                            {showPassHint &&
                                (passError ? (
                                    <p className="usuarios-pass-error">
                                        Debe tener al menos 6 caracteres, una mayúscula, minúscula,
                                        número y carácter especial.
                                    </p>
                                ) : (
                                    <p className="usuarios-pass-ok">Contraseña válida</p>
                                ))}
                        </div>
                    </div>

                    <hr className="usuarios-form-divider" />

                    {/* ── Información ── */}
                    <div className="usuarios-form-section">
                        <p className="usuarios-form-section-title">Información personal</p>
                        <div className="usuarios-form-grid">
                            <TextField
                                label="Nombre"
                                value={form.nombre}
                                onChange={set('nombre')}
                                fullWidth
                            />
                            <TextField
                                label="Apellidos"
                                value={form.apellido}
                                onChange={set('apellido')}
                                fullWidth
                            />
                            <TextField
                                label="Correo"
                                type="email"
                                value={form.email}
                                onChange={set('email')}
                                fullWidth
                            />
                            <TextField
                                label="Teléfono"
                                value={form.phoneNumber}
                                onChange={set('phoneNumber')}
                                fullWidth
                            />
                            <TextField
                                label="Notas"
                                value={form.notas}
                                onChange={set('notas')}
                                fullWidth
                                className="usuarios-form-full"
                            />
                        </div>
                    </div>

                    <hr className="usuarios-form-divider" />

                    {/* ── Configuración ── */}
                    <div className="usuarios-form-section">
                        <p className="usuarios-form-section-title">Configuración</p>
                        <div className="usuarios-form-grid">
                            <div>
                                <InputLabel>Grupo</InputLabel>
                                <Select
                                    fullWidth
                                    value={gpo}
                                    onChange={(e) => setGpo(e.target.value)}
                                >
                                    {gpos.map((i) => (
                                        <MenuItem key={i.id} value={i.id}>
                                            {i.nombre}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </div>
                            <div>
                                <InputLabel>Departamento</InputLabel>
                                <Select
                                    fullWidth
                                    value={depto}
                                    onChange={(e) => setDepto(e.target.value)}
                                >
                                    {deptos.map((i) => (
                                        <MenuItem key={i.id} value={i.id}>
                                            {i.nombre}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </div>
                            <div>
                                <InputLabel>Idioma</InputLabel>
                                <Select
                                    fullWidth
                                    value={language}
                                    onChange={(e) => setLanguage(e.target.value)}
                                >
                                    {languages.map((i) => (
                                        <MenuItem key={i.id} value={i.id}>
                                            {i.nombre}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </div>
                            <FormControl component="fieldset">
                                <FormLabel component="legend">Niveles</FormLabel>
                                <FormGroup row>
                                    {[
                                        ['one', 1],
                                        ['two', 2],
                                        ['three', 3],
                                    ].map(([key, n]) => (
                                        <FormControlLabel
                                            key={key}
                                            control={
                                                <Checkbox
                                                    checked={levels[key]}
                                                    onChange={(e) =>
                                                        setLevels((l) => ({
                                                            ...l,
                                                            [key]: e.target.checked,
                                                        }))
                                                    }
                                                />
                                            }
                                            label={`Nivel ${n}`}
                                        />
                                    ))}
                                </FormGroup>
                                <FormHelperText>Puede marcar más de uno</FormHelperText>
                            </FormControl>
                        </div>
                    </div>

                    <hr className="usuarios-form-divider" />

                    {/* ── Cargos ── */}
                    <div className="usuarios-form-section">
                        <p className="usuarios-form-section-title">Roles del usuario</p>
                        <div className="usuarios-roles-chips">
                            {roles.map((rol) => (
                                <Chip
                                    key={rol.nombre}
                                    label={rol.label ?? rol.nombre}
                                    clickable
                                    onClick={() => toggleRole(rol.nombre)}
                                    color={rol.color}
                                />
                            ))}
                        </div>
                    </div>
                </div>

                <div className="usuarios-modal-footer">
                    <Button
                        variant="outlined"
                        className="usuarios-modal-cancel"
                        onClick={handleClose}
                    >
                        Cancelar
                    </Button>
                    <Button
                        variant="contained"
                        color="primary"
                        className="usuarios-modal-submit"
                        onClick={createUser}
                    >
                        Crear usuario
                    </Button>
                </div>
            </div>
        </Modal>
    );
}

ModalNuevoUsuario.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
};
